import { Component, OnInit, signal, computed } from "@angular/core";
import { CommonModule, Location } from "@angular/common";
import { ActivatedRoute } from "@angular/router";
import ScheduleDetailHeader from "../../../../domains/jobs/components/shared/schedule-detail-header/schedule-detail-header.component";
import { SkeletonModule } from "primeng/skeleton";
import { db, SyncableStatus } from "../../../../core/database/db";
import { JobAssetDto } from "../../../../domains/media/models/JobAssetDto";
import { JobIssueDTO } from "../../../../domains/jobIssues/models/IssueDTO";
import { JobIssueService } from "../../../../domains/jobIssues/services/job-issue-service";
import { JobService } from "../../../../domains/jobs/services/job.service";
import { HeaderSectionComponent } from "../../components/header/header.component";
import { CompletedItemComponent, CompletedIssue, CompletedIssueExpansionState } from "./issue-item/issue-item.component";
import { UiToastService } from "../../../../shared/components/toast/ui-toast.service";
import { BlobService } from "../../../../shared/services/blob.service";
import { LoggerService } from "../../../../shared/services/logger.service";
import { ExtendedRouter } from "../../../../shared/services/extended-router.service";
import { VendorService } from "../../../../domains/vendors/services/vendor-service";
import { FilterDrawerComponent, FilterCategory, FilterValue, FilterMode } from "./filter-panel/filter-panel.component";
import { FilterService } from "./filter-panel/filter.service";
import { BottomSheetComponent } from "../../../../shared/components/bottom-sheet/bottom-sheet.component";
import { ButtonModule } from "primeng/button";


@Component({
  selector: "app-completed-items",
  standalone: true,
  imports: [
    CommonModule,
    ScheduleDetailHeader,
    HeaderSectionComponent,
    CompletedItemComponent,
    SkeletonModule,
    FilterDrawerComponent,
    BottomSheetComponent,
    ButtonModule,
  ],
  templateUrl: "./completed-items.component.html",
  styleUrls: ["./completed-items.component.scss"]
})

export class CompletedItemsComponent implements OnInit {
  lotId: number = 0;
  communityId: number = 0;
  loading = signal<boolean>(true);
  headerTitle: string = "Completed Items";
  headerSubtitle = signal<string>("");
  allExpanded = signal<boolean>(true);
  isItemsMenuOpen = false;

  issues = signal<CompletedIssue[]>([]);
  originalIssues = signal<CompletedIssue[]>([]);
  private objectUrls = new Set<string>();

  sortedIssues = computed(() => this.issues().sort((a, b) => {
    const getCompletedAtTime = (completedAt: Date | string | undefined): number => {
      if (!completedAt) {
        return 0;
      }
      if (completedAt instanceof Date) {
        return completedAt.getTime();
      }
      return new Date(completedAt).getTime();
    };

    const aCompletedAt = getCompletedAtTime(a.completedAt);
    const bCompletedAt = getCompletedAtTime(b.completedAt);

    if (aCompletedAt !== bCompletedAt) {
      return bCompletedAt - aCompletedAt; // Newest first
    }

    if (a.areaName && b.areaName) {
      return a.areaName.localeCompare(b.areaName);
    }
    if (a.areaName && !b.areaName) {
      return -1;
    }
    if (!a.areaName && b.areaName) {
      return 1;
    }
    return 0;
  }));

  vendors = signal<{ id: number; name: string }[]>([]);
  tradePartners = signal<string[]>([]);

  // Filter-related properties
  selectedFiltersCount = computed(() => this.filterService.selectedCount());
  hasAnyExpandedIssues = computed(() =>
    this.issues().some(i => i.expansionState !== CompletedIssueExpansionState.Collapsed)
  );
  showTradePartnerSubtext = signal<boolean>(false);

  shouldShowNoVendorsText = computed(() => {
    const hasIssues = this.originalIssues().length > 0;
    const hasTradePartners = this.uniqueTradePartners().length > 0;
    return this.showTradePartnerSubtext() && hasIssues && !hasTradePartners;
  });

  // Computed property to get unique trade partners from assigned vendors
  uniqueTradePartners = computed(() => {
    const refreshed = this.tradePartners();
    if (refreshed.length > 0) {
      return refreshed;
    }

    const tradePartners = new Set<string>();
    const vendorMap = this.vendorLookupMap();
    const issuesWithVendors = this.issuesWithVendors();
    issuesWithVendors.forEach(issue => {
      issue.jobIssueVendors!.forEach(vendor => {
        const vendorName = vendorMap.get(vendor.externalVendorId);
        if (vendorName) {
          tradePartners.add(vendorName);
        }
      });
    });
    return Array.from(tradePartners).sort();
  });

  // Helper computed properties
  private vendorLookupMap = computed(() => {
    const map = new Map<number, string>();
    this.vendors().forEach(vendor => {
      map.set(vendor.id, vendor.name);
    });
    return map;
  });

  private issuesWithVendors = computed(() =>
    this.originalIssues().filter(issue => issue.jobIssueVendors && issue.jobIssueVendors.length > 0)
  );

  // Filter categories (excluding Status)
  readonly FilterCategories: FilterCategory[] = [
    { id: "trade-partner", label: "Trade Partner", route: "/trade-partner" },
    { id: "room", label: "Room", route: "/room" }
  ];

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private router: ExtendedRouter,
    private vendorService: VendorService,
    private toastService: UiToastService,
    public filterService: FilterService,
    public blobService: BlobService,
    private logger: LoggerService,
    private jobIssueService: JobIssueService,
    private jobService: JobService
  ) {
  }

  async ngOnInit(): Promise<void> {
    this.filterService.clearAllFilters();
    this.lotId = Number(this.route.snapshot.paramMap.get("lotId"));
    this.communityId = Number(this.route.snapshot.paramMap.get("communityId"));
    await Promise.all([
      this.loadIssues(),
      this.loadJobDetails(),
      this.loadVendors()
    ]).finally(() => {
      this.loading.set(false);
      this.applyFilters();
    });
  }

  async loadIssues(): Promise<void> {
    try {
      const jobs = await this.jobService.getAllCompletedJobsForLot(this.lotId);
      const issues = (await this.jobIssueService.getLeafJobIssuesForJobs(jobs.map(job => job.id)))
        .filter(issue => !!issue.isComplete);

      if (issues.length === 0) {
        this.issues.set([]);
        this.originalIssues.set([]);
        return;
      }

      const enriched = await this.enrichCompletedIssues(issues as unknown as JobIssueDTO[]);
      this.originalIssues.set(enriched);
      this.issues.set(enriched);
    } catch (e) {
      this.logger.error("Error loading completed issues", e);
      this.issues.set([]);
      this.originalIssues.set([]);
    }
  }

  private async enrichCompletedIssues(issues: JobIssueDTO[]): Promise<CompletedIssue[]> {
    const enriched: CompletedIssue[] = [];

    for (const issue of issues) {
      const media = await db.media
        .where("parentIdentifier").equals(issue.identifier)
        .and(m => !m.deleted)
        .toArray();

      // Load jobIssueVendors for the issue
      const jobIssueVendors = await db.jobIssueVendor
        .where("issueIdentifier")
        .equals(issue.identifier)
        .and(v => !v.deleted)
        .toArray();

      const jobAssets: JobAssetDto[] = await Promise.all(media.map(async m => {
        let azureBlobUri = "";

        if (m.base64Image) {
          azureBlobUri = this.createObjectUrlAndTrack(this.blobService.base64ToBlob(m.base64Image));
        } else {
          // Look for any other media record with same fileIdentifier that has a blob
          const hydratedMedia = await db.media
            .where("fileIdentifier")
            .equals(m.fileIdentifier)
            .and(x => !!x.base64Image && !x.deleted)
            .first();

          if (hydratedMedia?.base64Image) {
            azureBlobUri = this.createObjectUrlAndTrack(this.blobService.base64ToBlob(hydratedMedia.base64Image));
          } else if (m.resourceUri) {
            azureBlobUri = m.resourceUri;
          }
        }

        const mappedJobAsset: JobAssetDto = {
          isUploaded: m.uploaded === 1,
          azureBlobUri,
          mediaGuid: m.fileIdentifier,
          updatedAt: m.updatedAt,
          deleted: m.deleted === 1,
          imageType: m.imageType
        };

        return mappedJobAsset;
      }));

      let areaName: string | undefined;
      if (issue.lotAreaIdentifier) {
        try {
          const area = await db.area
            .where("identifier")
            .equals(issue.lotAreaIdentifier)
            .first();
          areaName = area?.name;
        } catch { }
      }

      const enrichedIssue: CompletedIssue = {
        ...issue,
        text: issue.text,
        areaName,
        media: jobAssets,
        jobIssueVendors,
        expansionState: CompletedIssueExpansionState.Expanded,
      };

      enriched.push(enrichedIssue);
    }

    return enriched;
  }

  async loadJobDetails(): Promise<void> {
    try {
      const lot = await db.lot.get(this.lotId);

      if (!lot) {
        return;
      }

      const subtitleParts: string[] = [];
      if (lot.lotBlock) {
        subtitleParts.push(`Lot ${lot.lotBlock}`);
      }
      if (lot.streetAddress) {
        subtitleParts.push(lot.streetAddress);
      }
      if (lot.salesCommunityName) {
        subtitleParts.push(lot.salesCommunityName);
      }
      this.headerSubtitle.set(subtitleParts.join(", "));
    } catch { }
  }

  private async loadVendors(): Promise<void> {
    try {
      const vendorContacts = await this.vendorService.getVendorsForLotId(this.lotId);
      const vendorList = this.processVendorData(
        vendorContacts as unknown as Array<{ id: number; name: string; doingBusinessAs: string }>
      );
      this.vendors.set(vendorList);
    } catch (error) {
      this.logger.error("Failed to load vendors", error);
      this.vendors.set([]);
    }
  }

  private processVendorData(
    vendorContacts: Array<{ id: number; name: string; doingBusinessAs: string }>
  ): { id: number; name: string }[] {
    const allVendors = vendorContacts.flat();
    const uniqueVendors = new Map<number, {
      id: number;
      name: string;
      doingBusinessAs: string
    }>();
    allVendors.forEach(vendor => {
      if (!uniqueVendors.has(vendor.id)) {
        uniqueVendors.set(vendor.id, vendor);
      }
    });
    return Array.from(uniqueVendors.values()).map(vendor => ({
      id: vendor.id,
      name: vendor.doingBusinessAs || vendor.name,
    }));
  }

  onCollapseItemsToggle(areCollapsed: boolean): void {
    const nextIsExpanded = !areCollapsed;
    this.allExpanded.set(nextIsExpanded);
    this.issues.update(arr => arr.map(i => {
      const nextState = nextIsExpanded
        ? CompletedIssueExpansionState.Expanded
        : CompletedIssueExpansionState.Collapsed;
      return { ...i, expansionState: nextState };
    }));
  }

  updateIssueState(
    issue: CompletedIssue,
    newState: CompletedIssueExpansionState
  ): void {
    this.issues.update(items => items.map(i => {
      if (i.id === issue.id) {
        let finalState = newState;
        if (
          newState === CompletedIssueExpansionState.Collapsed &&
          i.expansionState === CompletedIssueExpansionState.FullyExpanded
        ) {
          finalState = this.allExpanded()
            ? CompletedIssueExpansionState.Expanded
            : CompletedIssueExpansionState.Collapsed;
        }
        return { ...i, expansionState: finalState };
      }

      if (newState === CompletedIssueExpansionState.FullyExpanded) {
        const otherState = this.allExpanded()
          ? CompletedIssueExpansionState.Expanded
          : CompletedIssueExpansionState.Collapsed;
        return { ...i, expansionState: otherState };
      }
      return i;
    }));
  }

  // Filter methods
  toggleFilter(): void {
    this.openFilterDrawer();
  }

  onFilterDrawerClose(): void {
    const hasActiveFilters = this.filterService.hasActiveFilters();

    if (hasActiveFilters) {
      this.filterService.resetToOriginal();
    } else {
      this.applyFilters();
    }

    this.filterService.closeFilterDrawer();
    this.showTradePartnerSubtext.set(false);
  }

  onFilterClearAll(): void {
    this.filterService.clearAllFilters();
    this.applyFilters();
  }

  onFilterApply(): void {
    this.applyFilters();
    this.filterService.closeFilterDrawer();
  }

  applyFilters(): void {
    const selected = this.filterService.selected();
    const allIssues = this.originalIssues();

    // If no filters are selected, show all issues
    if (!this.filterService.hasActiveFilters()) {
      // Preserve current expansion state when possible; otherwise derive from expand/collapse button
      const currentByIdentifier = new Map(this.issues().map(i => [i.identifier, i]));
      const defaultExpansion = this.allExpanded()
        ? CompletedIssueExpansionState.Expanded
        : CompletedIssueExpansionState.Collapsed;
      this.issues.set(
        allIssues.map(issue => ({
          ...issue,
          expansionState:
            currentByIdentifier.get(issue.identifier)?.expansionState
            ?? defaultExpansion
        })) as CompletedIssue[]
      );
      return;
    }

    // Filter issues based on selected criteria
    const filteredIssues = allIssues.filter(issue => {
      // Check trade partner filter (OR logic within category)
      if (selected["trade-partner"] && selected["trade-partner"].length > 0) {
        const hasMatchingTradePartner = issue.jobIssueVendors?.some(vendor => {
          const vendorName = this.vendorLookupMap().get(vendor.externalVendorId);
          if (vendorName) {
            return selected["trade-partner"].includes(vendorName);
          }
          return false;
        });
        if (!hasMatchingTradePartner) {
          return false;
        }
      }

      // Check room filter (OR logic within category)
      if (selected["room"] && selected["room"].length > 0) {
        if (!issue.areaName || !selected["room"].includes(issue.areaName)) {
          return false;
        }
      }

      return true;
    });

    // Preserve expansion state for filtered list using current visible issues; fallback to expand/collapse button
    const currentByIdentifier = new Map(this.issues().map(i => [i.identifier, i]));
    const defaultExpansion = this.allExpanded()
      ? CompletedIssueExpansionState.Expanded
      : CompletedIssueExpansionState.Collapsed;
    this.issues.set(
      filteredIssues.map(issue => ({
        ...issue,
        expansionState:
          currentByIdentifier.get(issue.identifier)?.expansionState
          ?? defaultExpansion
      })) as CompletedIssue[]
    );
  }

  onFilterCategoryClick(option: FilterCategory): void {
    // Check if it's Trade Partner with no trade partners - show subtext
    if (option.id === "trade-partner" && this.uniqueTradePartners().length === 0) {
      this.showTradePartnerSubtext.set(true);
      return;
    }

    const selectedValues = this.filterService.selected()[option.id] || [];
    this.filterService.enterConfigurationMode(option, selectedValues);

    // Generate filter values using strategy pattern with data accessors
    const values = this.filterService.generateFilterValuesForCategory(
      option,
      selectedValues,
      {
        getTradePartners: () => this.uniqueTradePartners(),
        getAreas: () => this.uniqueAreas()
      }
    );
    this.filterService.setValues(values);
  }

  onFilterValueToggle(value: FilterValue): void {
    this.filterService.toggleFilterValue(value);
  }

  onFilterModeChange(event: { mode: FilterMode; action: "select" | "cancel" }): void {
    const { mode } = event;
    this.filterService.setMode(mode);

    if (mode === "selection") {
      const newSelected = this.filterService.exitConfigurationMode(event.action);
      this.filterService.setSelected(newSelected);
      this.filterService.setCurrentType(null);
      this.filterService.setValues([]);
    }
  }

  openFilterDrawer(): void {
    this.filterService.openFilterDrawer();
  }

  onRemoveFilterPill(filterType: string, filterId: string): void {
    this.filterService.removeFilterPill(filterType, filterId);
    this.applyFilters();
  }

  uniqueAreas(): string[] {
    const areaNames: string[] = [];
    for (const issue of this.originalIssues()) {
      if (issue.areaName) {
        areaNames.push(issue.areaName);
      }
    }
    return [...new Set(areaNames)].sort();
  }

  goBack(): void {
    this.router.navigateWithPersistedState([
      "/cm/all-my-lots/community", this.communityId, "lot", this.lotId, "summary"
    ]);
  }

  openItemsMenu(): void {
    this.isItemsMenuOpen = true;
  }

  navigateToOpenItems(): void {
    this.isItemsMenuOpen = false;
    this.router.navigateWithPersistedState([
      "/cm/all-my-lots/community", this.communityId, "lot", this.lotId, "open-items", "list"
    ]);
  }

  private calculateDueDate(): Date {
    const dueDate = new Date();
    let weekdaysAdded = 0;

    while (weekdaysAdded < 2) {
      dueDate.setDate(dueDate.getDate() + 1);
      const dayOfWeek = dueDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        weekdaysAdded += 1;
      }
    }

    return dueDate;
  }

  async reopenIssue(issue: CompletedIssue): Promise<void> {
    if (!issue.id) {
      this.logger.error("Cannot reopen issue without ID");
      return;
    }

    try {
      // Calculate due date (2 weekdays from now)
      const dueDate = this.calculateDueDate();

      // Use database transaction to ensure consistency
      await db.transaction("readwrite", ["jobIssue", "jobIssueVendor", "media", "syncStatus"], async () => {
        // Update the issue
        await db.jobIssue.update(issue.id!, {
          isComplete: false,
          completedAt: undefined,
          isSelfAssigned: false,
          dueDate,
          syncStatus: SyncableStatus.Pending,
          updatedAt: new Date()
        });

        // Mark all associated vendors as deleted
        await db.jobIssueVendor
          .where("issueIdentifier")
          .equals(issue.identifier)
          .modify({
            deleted: true,
            syncStatus: SyncableStatus.Pending,
            updatedAt: new Date()
          });
      });

      // Remove the issue from the completed items list
      this.issues.update(items => items.filter(i => i.id !== issue.id));
      this.originalIssues.update(items => items.filter(i => i.id !== issue.id));

      // Show success toast
      this.toastService.success("Item reopened and moved to Open Items.");

    } catch (error) {
      this.logger.error("Error reopening issue", error);
      this.toastService.error("Failed to reopen item. Please try again.");
    }
  }

  private createObjectUrlAndTrack(blob: Blob): string {
    const url = URL.createObjectURL(blob);
    this.objectUrls.add(url);
    return url;
  }
}


